'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '@/utils';
import { Button } from '../button';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black-200/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showBackdrop?: boolean;
    overlayClassName?: string;
  }
>(
  (
    { className, children, showBackdrop = true, overlayClassName, ...props },
    ref
  ) => (
    <DialogPortal>
      {showBackdrop && <DialogOverlay className={overlayClassName} />}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-1 border rounded-20 bg-white p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

interface DialogHeaderProps extends React.ComponentPropsWithoutRef<'div'> {
  title?: string;
  description?: string;
  closeButton?: boolean;
  titleRender?: React.ReactNode;
}

const DialogHeader = ({
  className,
  children,
  title,
  description,
  closeButton = true,
  titleRender,
  ...props
}: DialogHeaderProps) => (
  <div data-color-scheme="dark" className="group flex-initial flex-shrink-0">
    <div
      className={cn(
        'flex flex-col bg-primary text-black text-center p-1.5 pb-6',
        className
      )}
      {...props}
    >
      <div>
        {title && (
          <DialogTitle asChild>
            <span className="text-14 font-normal">{title}</span>
          </DialogTitle>
        )}
        {titleRender && titleRender}
        {description && (
          <DialogDescription asChild>
            <span className="text-white/70 pt-1.5 pr-1.5 text-14 font-normal">
              {description}
            </span>
          </DialogDescription>
        )}
      </div>
      {closeButton && (
        <DialogPrimitive.Close asChild className="absolute right-5 top-5">
          <Button isIcon>
            <div className="size-6 cursor-pointer">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.18972 5.18919C5.43379 4.94511 5.82952 4.94511 6.0736 5.18919L10.0005 9.11612L13.9274 5.18919C14.1715 4.94511 14.5673 4.94511 14.8113 5.18919C15.0554 5.43327 15.0554 5.829 14.8113 6.07307L10.8844 10L14.8113 13.9269C15.0554 14.171 15.0554 14.5667 14.8113 14.8108C14.5673 15.0549 14.1715 15.0549 13.9274 14.8108L10.0005 10.8839L6.0736 14.8108C5.82952 15.0549 5.43379 15.0549 5.18972 14.8108C4.94564 14.5667 4.94564 14.171 5.18972 13.9269L9.11664 10L5.18972 6.07307C4.94564 5.829 4.94564 5.43327 5.18972 5.18919Z"
                  fill="#000"
                />
              </svg>
            </div>
            <span className="sr-only">Close</span>
          </Button>
        </DialogPrimitive.Close>
      )}
      {children}
    </div>
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
